import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useObjectTableColumns } from 'components/ObjectTable/ObjectTable';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useSortColumnsModal } from 'hooks/useSortColumnsModal';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { mapValues } from 'lodash';
import { type TFindObjectsPayload, findMoreObjects } from 'slices/object.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import type { TOrganizationNodeDrawer } from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import { canManageObjects } from 'utils/authentication';
import { parseCustomerSignature } from 'utils/customerSignature';
import { createTypeOptions } from 'utils/objectTypes';
import type { TTEObject } from '@timeedit/types';
import {
  EditableTable,
  type GetMore,
  type GetMoreOptions,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import {
  Button,
  Form,
  TreeSelect,
  Typography,
} from '@timeedit/ui-components/antd';
import './ObjectsCard.scss';

type OrgDrawerContentProps = {
  data: TOrganizationNodeDrawer;
};

const LIMIT_PER_PAGE = 50;
const ITEM_HEIGHT = 36;
const LIST_HEIGHT = 300;

export const ObjectsCard = ({ data: value }: OrgDrawerContentProps) => {
  return (
    <>
      <Typography.Paragraph>
        Objects associated with the organization node.
      </Typography.Paragraph>
      <ObjectsPreviewTable organizationId={value.id} />
    </>
  );
};

const LinkToObjectManager: FC<
  PropsWithChildren<{ organizationId: string; typeId?: number }>
> = (props) => {
  const user = useAppSelector((state) => state.auth.user);

  const path = useMemo(() => {
    const signature = parseCustomerSignature(user.customerSignature);
    if (!signature) return null;

    const params = new URLSearchParams();
    params.append('orgNodeId', props.organizationId);
    if (props.typeId) {
      params.append('typeId', props.typeId.toString());
    }

    return `/${signature}/objects?${params}`;
  }, [user, props.organizationId, props.typeId]);

  const hasAccess = useMemo(() => canManageObjects(user), [user]);

  if (!hasAccess || !path) {
    return null;
  }

  return (
    <Typography.Link underline href={path} target="_blank">
      {props.children}
    </Typography.Link>
  );
};

const ObjectsPreviewTable: FC<{ organizationId: string }> = (props) => {
  const [objectTypeId, setObjectTypeId] = useState<number | null>(null);

  const getMoreObjects = useCallback<GetMore<TTEObject>>(
    async (options: GetMoreOptions) => {
      if (!props.organizationId) {
        return {
          data: [],
          page: 0,
          count: 0,
          total: 0,
          hasMore: false,
        };
      }
      const { results, page, totalPages, totalResults } = await findMoreObjects(
        {
          organizations: [props.organizationId],
          typeId: objectTypeId != null ? objectTypeId : undefined,
          active: 'TRUE',
          // `useObjectTableColumns` renders reference columns for this table
          includeReferenceFields: true,
          ...options,
        } satisfies TFindObjectsPayload
      );
      return {
        data: results,
        page,
        count: results.length,
        total: totalResults,
        hasMore: page < totalPages,
      };
    },
    [props.organizationId, objectTypeId]
  );

  const user = useAppSelector((state) => state.auth.user);

  const objectPath = useMemo(() => {
    const signature = parseCustomerSignature(user?.customerSignature);
    if (!signature) return null;

    return `/${signature}/objects`;
  }, [user?.customerSignature]);

  const columns = useObjectTableColumns(objectTypeId);

  const updatedColumns = useMemo(() => {
    const unsortableObjects = mapValues(columns, (col) => ({
      ...col,
      sorter: undefined,
      ellipsis: true,
    }));

    unsortableObjects.active.ellipsis = false;

    if (objectPath) {
      unsortableObjects.extId.render = (extId, obj) => (
        <Typography.Link
          underline
          href={`${objectPath}?item1=${obj.id}`}
          target="_blank"
        >
          {extId}
        </Typography.Link>
      );
    }
    return unsortableObjects;
  }, [columns, objectPath]);

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    useSortColumnsModal(updatedColumns);

  const {
    data: objects = [],
    onScroll,
    reset,
    total: filteredAmount,
    loading,
  } = useInfiniteScroll<TTEObject>({
    className: '.rc-virtual-list-holder-inner',
    getMore: getMoreObjects,
    itemHeight: ITEM_HEIGHT,
    containerHeight: LIST_HEIGHT,
    limit: LIMIT_PER_PAGE,
    virtual: true,
    initialFetch: true,
  });

  useEffect(() => {
    reset();
  }, [props.organizationId, objectTypeId, reset]);

  const total = useTotal(props.organizationId);

  const titleActions = useMemo(
    () => ({
      right: (
        <Button onClick={openSortColumnsModal} size="small">
          {getText('ui.table.edit_columns')}
        </Button>
      ),
    }),
    [openSortColumnsModal]
  );

  const title = useTableTitle({
    total,
    filteredAmount,
    actions: titleActions,
    resultType: 'objects',
  });

  const [ref, defaultTableProps] = useDefaultTableProps(0);

  return (
    <div className="objects-card-container">
      <TableFilter
        organizationId={props.organizationId}
        typeId={objectTypeId}
        onChange={setObjectTypeId}
      />
      <div ref={ref} style={{ height: `${LIST_HEIGHT}px` }}>
        <EditableTable
          {...defaultTableProps}
          loading={loading}
          title={title}
          dataSource={objects}
          columns={sortedColumns as any}
          virtual={true}
          fitColumns={false}
          onScroll={onScroll}
        />
        {sortColumnsModal}
      </div>
    </div>
  );
};

const useTotal = (organizationId: string) => {
  const [total, setTotal] = useState(0);

  const fetchTotal = useCallback(async () => {
    const { totalResults } = await findMoreObjects({
      organizations: [organizationId],
      active: 'TRUE',
      limit: 1,
    } satisfies TFindObjectsPayload);
    setTotal(totalResults);
  }, [organizationId]);

  useEffect(() => {
    fetchTotal();
  }, [fetchTotal]);

  return total;
};

const TableFilter: FC<{
  typeId: number | null;
  organizationId: string;
  onChange: (value: number) => void;
}> = (props) => {
  const allObjectTypes = useAppSelector(selectObjectTypesList);
  const objectTypeOptions = useMemo(
    () => createTypeOptions(allObjectTypes),
    [allObjectTypes]
  );

  useEffect(() => {
    if (props.typeId == null && allObjectTypes.length > 0) {
      props.onChange(allObjectTypes[0].id);
    }
  }, [props.typeId, allObjectTypes, props.onChange]);

  return (
    <div className="objects-card-filter">
      <Form.Item label="Object Type">
        <TreeSelect
          treeData={objectTypeOptions}
          value={props.typeId?.toString() ?? null}
          onChange={(v) => props.onChange(parseInt(v, 10))}
          popupMatchSelectWidth={false}
          treeDefaultExpandedKeys={objectTypeOptions.map((opt) => opt.value)}
        />
      </Form.Item>
      <LinkToObjectManager
        typeId={props.typeId}
        organizationId={props.organizationId}
      >
        Manage objects
      </LinkToObjectManager>
    </div>
  );
};
